import React, { useState, useEffect } from 'react';
import { X, Plus, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '@/components/ui/Button';
import InputField from '@/components/common/InputField';
import { Column } from '@/types/columns';
import { toastMessage } from '@/utils/toast-message';
import { fetchLeadColumns, createLead } from '@/actions/lead-manager';
import ColumnsService from '@/utils/columnsCache';

interface FormValues {
    [key: string]: string | number | null;
}

interface DuplicateLeadInfo {
    leadId: number;
    leadUuid: string;
    phone: string;
    name: string;
    email: string;
    createdDate: string;
    owner: string | null;
    ownerUuid: string | null;
}

interface AddLeadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    refreshData?: () => void;
}

const AddLeadModal: React.FC<AddLeadModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    refreshData
}) => {
    const [columns, setColumns] = useState<Column[]>([]);
    const [loading, setLoading] = useState(false);
    const [formValues, setFormValues] = useState<FormValues>({});
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
    const [duplicateError, setDuplicateError] = useState<DuplicateLeadInfo | null>(null);
    const [duplicateDetailsExpanded, setDuplicateDetailsExpanded] = useState(true);

    // Fetch columns when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchFormColumns();
        }
    }, [isOpen]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setFormValues({});
            setFieldErrors({});
            setDuplicateError(null);
        }
    }, [isOpen]);

    const fetchFormColumns = async () => {
        try {
            setLoading(true);
            let response = await ColumnsService.getColumns(true);

            if (!response.success) {
                response = await fetchLeadColumns();
            }

            if (response.success && response.columns) {
                const activeColumns = response.columns;
                setColumns(activeColumns);

                // Initialize form values with defaults
                const initialValues: FormValues = {};
                activeColumns.forEach(column => {
                    if (column.col_type === 'select' && column.options.length > 0) {
                        // Set default to first option or "New Lead" status
                        if (column.col_name === 'status') {
                            const newLeadOption = column.options.find(opt =>
                                opt.opt_name.toLowerCase().includes('new') ||
                                opt.opt_name.toLowerCase().includes('lead')
                            );
                            initialValues[column.uuid] = newLeadOption?.uuid || column.options[0]?.uuid || '';
                        } else if (column.col_name === 'source') {
                            // Default source to "Agent" for agent-created leads
                            const agentOption = column.options.find(opt =>
                                opt.opt_value === 'agent' || opt.opt_value === 'walkin' ||
                                opt.opt_name.toLowerCase() === 'agent' || opt.opt_name.toLowerCase() === 'walkin'
                            );
                            initialValues[column.uuid] = agentOption?.uuid || column.options[0]?.uuid || '';
                        } else {
                            const defaultOption = column.options.find(opt => opt.opt_value === 'please_select');
                            initialValues[column.uuid] = defaultOption?.uuid || '';
                        }
                    } else if (column.col_name === 'created_date' || column.col_name === 'created_at' || column.col_name === 'createdAt') {
                        // Set created date to current local datetime (IST)
                        const now = new Date();
                        const year = now.getFullYear();
                        const month = String(now.getMonth() + 1).padStart(2, '0');
                        const day = String(now.getDate()).padStart(2, '0');
                        const hours = String(now.getHours()).padStart(2, '0');
                        const minutes = String(now.getMinutes()).padStart(2, '0');
                        const localDatetime = `${year}-${month}-${day}T${hours}:${minutes}`;
                        initialValues[column.uuid] = localDatetime;
                    } else {
                        initialValues[column.uuid] = '';
                    }
                });

                setFormValues(initialValues);
            }
        } catch (error) {
            console.error('Error fetching columns:', error);
            toastMessage('Failed to load form fields', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (uuid: string, value: string | number, colName?: string, colType?: string) => {
        let processedValue = value;

        // For phone fields, only allow digits and limit to 10
        if (colName === 'phone') {
            const digitsOnly = String(value).replace(/\D/g, '');
            processedValue = digitsOnly.slice(0, 10);
        } else if (typeof value === 'string' && colType !== 'select' && colType !== 'textarea') {
            // For text fields: trim leading/trailing spaces and collapse multiple spaces
            processedValue = value.replace(/^\s+/, '').replace(/\s+$/, '').replace(/\s{2,}/g, ' ');
        }

        setFormValues(prev => ({
            ...prev,
            [uuid]: processedValue
        }));

        // Clear error for this field
        if (fieldErrors[uuid]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[uuid];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate required fields
        const requiredFields = columns.filter(col => col.col_required);
        const missingFields = requiredFields.filter(field => {
            const value = formValues[field.uuid];
            return value === null || value === undefined || value === '';
        });

        if (missingFields.length > 0) {
            const newErrors: { [key: string]: string } = {};
            missingFields.forEach(field => {
                newErrors[field.uuid] = `${field.col_label} is required`;
            });
            setFieldErrors(newErrors);
            toastMessage(`Please fill in required fields: ${missingFields.map(f => f.col_label).join(', ')}`, 'error');
            return;
        }

        // Validate phone fields - must be exactly 10 digits
        const phoneFields = columns.filter(col => col.col_name === 'phone');
        const invalidPhoneFields = phoneFields.filter(field => {
            const value = String(formValues[field.uuid] || '').replace(/\D/g, '');
            // Only validate if field has a value (skip empty non-required fields)
            return value.length > 0 && value.length !== 10;
        });

        if (invalidPhoneFields.length > 0) {
            const newErrors: { [key: string]: string } = { ...fieldErrors };
            invalidPhoneFields.forEach(field => {
                newErrors[field.uuid] = 'Phone number must be exactly 10 digits';
            });
            setFieldErrors(newErrors);
            toastMessage('Phone number must be exactly 10 digits', 'error');
            return;
        }

        try {
            setLoading(true);
            setDuplicateError(null);

            const formData: { [key: string]: any } = {};
            columns.forEach(column => {
                // Skip created_date - let backend set it with CURRENT_TIMESTAMP
                if (column.col_name === 'created_date' || column.col_name === 'created_at' || column.col_name === 'createdAt') {
                    return;
                }
                let value = formValues[column.uuid];
                // Trim string values before submitting
                if (typeof value === 'string') {
                    value = value.trim();
                }
                formData[column.uuid] = value;
            });

            const createData = { formData };
            const response = await createLead(createData);

            if (response.success) {
                toastMessage(response.message || 'Lead created successfully!', 'success');

                // Reset form
                setFormValues({});
                setFieldErrors({});

                // Close modal
                onClose();

                // Refresh data
                if (refreshData) {
                    await refreshData();
                }

                // Call success callback
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                // Check if it's a duplicate phone number error
                // existingLead may be in response directly or in response.data depending on API wrapper
                const responseData = response.data as Record<string, unknown> | undefined;
                const rawExistingLead = response.existingLead || responseData?.existingLead;
                const errorCode = response.error || (responseData?.error as string | undefined);
                if (errorCode === 'DUPLICATE_PHONE_NUMBER' && rawExistingLead) {
                    const existingLead = rawExistingLead as Record<string, unknown>;
                    setDuplicateError({
                        leadId: (existingLead.leadId as number) || 0,
                        leadUuid: (existingLead.leadUuid as string) || '',
                        phone: (existingLead.phone as string) || '',
                        name: (existingLead.name as string) || '',
                        email: (existingLead.email as string) || '',
                        createdDate: (existingLead.createdDate as string) || '',
                        owner: (existingLead.owner as string) || null,
                        ownerUuid: (existingLead.ownerUuid as string) || null,
                    });
                } else {
                    toastMessage(response.message || 'Failed to create lead', 'error');
                }
            }
        } catch (error) {
            console.error('Error creating lead:', error);
            toastMessage('An error occurred while creating the lead', 'error');
        } finally {
            setLoading(false);
        }
    };

    const mapOptions = (options: any[]) => {
        return options
            .sort((a, b) => a.opt_order - b.opt_order)
            .map(option => ({
                value: option.uuid,
                label: option.opt_name
            }));
    };

    const renderField = (column: Column) => {
        const { uuid, col_label, col_type, col_required, col_description, options, col_name } = column;

        const fieldValue = formValues[uuid] !== null && formValues[uuid] !== undefined
            ? String(formValues[uuid])
            : '';

        // Check if this is the created date field (should be read-only)
        const isCreatedDateField = col_name === 'created_date' || col_name === 'created_at' || col_name === 'createdAt';

        const commonProps = {
            id: uuid,
            name: uuid,
            required: col_required,
            disabled: loading || isCreatedDateField,
            readOnly: isCreatedDateField,
            placeholder: col_description || `Enter ${col_label}`,
            value: fieldValue,
            onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
                if (!isCreatedDateField) {
                    handleChange(uuid, e.target.value, col_name, col_type);
                }
            },
            error: !!fieldErrors[uuid],
            className: `w-full ${isCreatedDateField ? 'bg-gray-100 cursor-not-allowed' : ''}`
        };

        // Handle phone field by col_name (strict 10 digit validation)
        if (col_name === 'phone') {
            const digitCount = fieldValue.replace(/\D/g, '').length;
            return (
                <div>
                    <InputField
                        {...commonProps}
                        type="tel"
                        placeholder="Enter 10 digit phone number"
                        maxLength={10}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            handleChange(uuid, e.target.value, col_name);
                        }}
                    />
                    <div className={`text-xs mt-1 ${digitCount === 10 ? 'text-green-600' : digitCount > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                        {digitCount}/10 digits {digitCount === 10 && '✓'}
                    </div>
                    {fieldErrors[uuid] && (
                        <div className="text-xs text-red-500 mt-1">{fieldErrors[uuid]}</div>
                    )}
                </div>
            );
        }

        switch (col_type) {
            case 'select':
                return (
                    <InputField
                        {...commonProps}
                        type="select"
                        options={mapOptions(options)}
                    />
                );
            case 'textarea':
                return (
                    <InputField
                        {...commonProps}
                        type="textarea"
                        rows={3}
                    />
                );
            case 'dateTime':
                return (
                    <InputField
                        {...commonProps}
                        type="datetime-local"
                    />
                );
            case 'date':
                return (
                    <InputField
                        {...commonProps}
                        type="date"
                    />
                );
            case 'email':
                return (
                    <InputField
                        {...commonProps}
                        type="email"
                        placeholder="example@email.com"
                    />
                );
            case 'number':
            case 'numeric':
                return (
                    <InputField
                        {...commonProps}
                        type="number"
                    />
                );
            case 'phone':
            case 'tel':
                return (
                    <InputField
                        {...commonProps}
                        type="tel"
                        placeholder="Enter phone number"
                    />
                );
            case 'url':
                return (
                    <InputField
                        {...commonProps}
                        type="text"
                        placeholder="https://example.com"
                    />
                );
            default:
                return (
                    <InputField
                        {...commonProps}
                        type="text"
                    />
                );
        }
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 ${
                    isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
                }`}
                onClick={onClose}
            />

            {/* Slider Panel */}
            <div
                className={`fixed top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                {/* Header - Sticky (matches top nav height) */}
                <div className="flex-shrink-0 flex items-center justify-between px-5 h-16 bg-brandColor">
                    <div className="flex items-center gap-2">
                        <Plus className="h-5 w-5 text-white" />
                        <h2 className="text-lg font-semibold text-white">Add New Lead</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Duplicate Lead Error */}
                    {duplicateError && (
                        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setDuplicateDetailsExpanded(!duplicateDetailsExpanded)}
                                className="w-full p-3 flex items-center justify-between hover:bg-amber-100 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                                    <span className="text-sm font-semibold text-amber-800">
                                        Lead already exists in the system. Please connect with the admin.
                                    </span>
                                </div>
                                {duplicateDetailsExpanded ? (
                                    <ChevronUp className="h-4 w-4 text-amber-600" />
                                ) : (
                                    <ChevronDown className="h-4 w-4 text-amber-600" />
                                )}
                            </button>
                            {duplicateDetailsExpanded && (
                                <div className="px-3 pb-3 pt-0">
                                    <div className="text-sm text-amber-700 space-y-1 pl-6">
                                        <p><span className="font-medium">Name:</span> {duplicateError.name || '-'}</p>
                                        <p><span className="font-medium">Phone:</span> {duplicateError.phone}</p>
                                        <p><span className="font-medium">Email:</span> {duplicateError.email || '-'}</p>
                                        <p><span className="font-medium">Owner:</span> {duplicateError.owner || 'Unassigned'}</p>
                                        <p><span className="font-medium">Created:</span> {duplicateError.createdDate}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {loading && columns.length === 0 ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
                            <span className="ml-3 text-gray-600">Loading form fields...</span>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Form Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {columns
                                    .sort((a, b) => a.col_order - b.col_order)
                                    .map((column) => (
                                        <div key={column.uuid} className={`${
                                            column.col_type === 'textarea' ? 'md:col-span-2' : ''
                                        }`}>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {column.col_label}
                                                {column.col_required && <span className="text-red-500 ml-1">*</span>}
                                            </label>
                                            {renderField(column)}
                                        </div>
                                    ))}
                            </div>
                        </form>
                    )}
                </div>

                {/* Footer - Sticky */}
                <div className="flex-shrink-0 flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || columns.length === 0}
                        className="bg-brandColor text-white hover:bg-brandColor"
                    >
                        {loading ? 'Creating...' : 'Create Lead'}
                    </Button>
                </div>
            </div>
        </>
    );
};

export default React.memo(AddLeadModal);